import { createColumnHelper, type CellContext, type ColumnDef } from '@tanstack/react-table';
import {
  Button,
  FormattedDateTime,
  InlineStack,
  Text,
  Truncated,
  type DataTableFeatures,
} from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import { formatDefaultTimestamp, formatLongTimestamp } from '@/utils/calendar-timestamps';
import type { WorkspaceObjectType } from '@/features/workspaces';
import { isObjectLog, NameCell, ObjectCell, PersonCell, TypeCell } from './cells';
import type { IdentifierConfig } from '../display-name';
import type { ActivityLogRow } from '../types';

const helper = createColumnHelper<DataTableFeatures, ActivityLogRow>();

/** Resolves a row's human-readable Activity Type label (taxonomy-aware; supplied by the page). */
export type ResolveTypeLabel = (row: ActivityLogRow) => string;

/** Dependencies the identified columns need from the page (taxonomy + workspace-scoped links). */
export interface IdentifiedColumnDeps {
  resolveTypeLabel: ResolveTypeLabel;
  workspaceId: string;
  personSingular: string;
  objectTypeById: Map<number, WorkspaceObjectType>;
  /** Builder-plan accounts have no objects, so the header is just the person term. */
  isBuilder: boolean;
  /** Workspace identifier config, for the Activity Name `(N identifier)` count. */
  identifiers: IdentifierConfig;
}

type TimestampCellProps = { value: number | null | undefined };

export function TimestampCell({ value }: TimestampCellProps) {
  if (value == null)
    return (
      <Text size="xxs" color="secondary">
        —
      </Text>
    );
  // Relative time + hover tooltip with the absolute date/time; FormattedDateTime auto-detects
  // epoch seconds vs ms.
  return (
    <FormattedDateTime
      date={value}
      format="medium_with_time"
      withRelativeDateFromNow
      withTooltip
      size="xxs"
      color="secondary"
    />
  );
}

type CalendarTimestampProps = { value: number | null | undefined };

/**
 * When a log was recorded, as Ember's activity-log rows show it: moment's calendar format
 * ("Yesterday at 1:04:19 pm (EDT)"), cut short to fit the cell, with the absolute date in a
 * tooltip once it is cut short (`activity-log-row/timestamp/index.gts`).
 */
function CalendarTimestamp({ value }: CalendarTimestampProps) {
  if (value == null) {
    return (
      <Text size="xxs" color="secondary">
        —
      </Text>
    );
  }
  return (
    <Truncated size="xxs" color="secondary" tooltip={formatDefaultTimestamp(value)}>
      {formatLongTimestamp(value)}
    </Truncated>
  );
}

type RecentActivityTimestampCellProps = CellContext<DataTableFeatures, ActivityLogRow>;

/**
 * When the log was recorded, and the row's expander, as the data index's Ember
 * panels draw both: moment's calendar format — "Today at 3:51:14 pm (PDT)" —
 * with the absolute date as the tooltip when it is cut short, and a chevron at
 * the end of the cell rather than a column of its own
 * (`activity-log-row/timestamp/index.gts`, `@displayExpansionArrow`). The
 * whole row still toggles on a click; the chevron is a button so the keyboard
 * can reach it too.
 */
function RecentActivityTimestampCell({ row }: RecentActivityTimestampCellProps) {
  const timestamp = row.original.log.timestamp;
  const isExpanded = row.getIsExpanded();

  return (
    // `min-width: 0`, as Ember's wrapper sets, so the text can truncate
    // rather than push the chevron out of a narrow cell.
    <InlineStack alignInline="end" alignBlock="center" gap="100" minW="0">
      <CalendarTimestamp value={timestamp} />
      <Button
        variant="tertiary"
        size="sm"
        isIconOnly
        icon={isExpanded ? 'chevron-up' : 'chevron-down'}
        aria-label={isExpanded ? 'Collapse' : 'Expand'}
        aria-expanded={isExpanded}
        onClick={(event: React.MouseEvent) => {
          // The row toggles on a click of its own, which would undo this one.
          event.stopPropagation();
          row.toggleExpanded();
        }}
      />
    </InlineStack>
  );
}

/**
 * Identified tab: {Person|Profile}/Object · Activity Type · Activity Name · Timestamp · Processed At.
 */
/**
 * The two columns a panel shows when it is reporting one item's own activity:
 * who or what the row is about, and when. The subject cell reads the log
 * rather than the surface, which is the same decision the whole feed makes.
 */
export function createRecentActivityColumns({
  workspaceId,
  personSingular,
  isObject,
  objectTypeById,
  subjectHeader,
}: RecentActivityColumnsOptions): ColumnDef<DataTableFeatures, ActivityLogRow>[] {
  return [
    {
      id: 'subject',
      // The header names what the item is about; each row names what that row
      // is about. A relationship attribute is not object-scoped, but its logs
      // still carry an object.
      header: subjectHeader ?? (isObject ? 'Object' : personSingular),
      cell: ({ row }: { row: { original: ActivityLogRow } }) =>
        isObjectLog(row.original.log) ? (
          <ObjectCell row={row.original} workspaceId={workspaceId} objectTypeById={objectTypeById} />
        ) : (
          <PersonCell row={row.original} workspaceId={workspaceId} personSingular={personSingular} />
        ),
    },
    {
      id: 'timestamp',
      header: 'Timestamp',
      meta: { align: 'end' },
      cell: RecentActivityTimestampCell,
    },
  ];
}

type RecentActivityColumnsOptions = {
  workspaceId: string;
  personSingular: string;
  /** Whether the item this reports on belongs to an object type. */
  isObject: boolean;
  objectTypeById: Map<number, WorkspaceObjectType>;
  /** What the first column is headed, where the surface names its rows by something else. */
  subjectHeader?: string;
};

export function createIdentifiedColumns({
  resolveTypeLabel,
  workspaceId,
  personSingular,
  objectTypeById,
  isBuilder,
  identifiers,
}: IdentifiedColumnDeps) {
  // The person term, suffixed with `/Object` unless the account is on the Builder plan (which has
  // no objects). The term follows the taxonomy rename (Person → "Profile"), same as the page title.
  const personObjectHeader = isBuilder ? personSingular : `${personSingular}/object`;
  return [
    helper.display({
      id: 'person',
      header: personObjectHeader,
      cell: (info) =>
        isObjectLog(info.row.original.log) ? (
          <ObjectCell row={info.row.original} workspaceId={workspaceId} objectTypeById={objectTypeById} />
        ) : (
          <PersonCell row={info.row.original} workspaceId={workspaceId} personSingular={personSingular} />
        ),
    }),
    helper.display({
      id: 'type',
      header: 'Activity type',
      cell: (info) => (
        <TypeCell
          row={info.row.original}
          label={resolveTypeLabel(info.row.original)}
          workspaceId={workspaceId}
          personSingular={personSingular}
        />
      ),
    }),
    helper.display({
      id: 'name',
      header: 'Activity name',
      cell: (info) => <NameCell row={info.row.original} workspaceId={workspaceId} identifiers={identifiers} />,
    }),
    helper.accessor((row) => row.log.timestamp, {
      id: 'timestamp',
      header: 'Timestamp',
      cell: (info) => <TimestampCell value={info.getValue()} />,
    }),
    helper.accessor((row) => row.log.processed_at, {
      id: 'processed_at',
      header: 'Processed at',
      cell: (info) => <TimestampCell value={info.getValue()} />,
    }),
  ];
}

/** Anonymous tab: Anonymous ID · Type · Name · Timestamp (no Person, no Processed At). */
export function createAnonymousColumns({
  resolveTypeLabel,
  workspaceId,
  personSingular,
}: {
  resolveTypeLabel: ResolveTypeLabel;
  workspaceId: string;
  personSingular: string;
}) {
  return [
    helper.accessor((row) => row.log.customer_id, {
      id: 'anonymous_id',
      header: 'Anonymous ID',
      cell: (info) => <Text size="sm">{isEmptyString(info.getValue() ?? '') ? '—' : info.getValue()}</Text>,
    }),
    helper.display({
      id: 'type',
      header: 'Type',
      cell: (info) => (
        <TypeCell
          row={info.row.original}
          label={resolveTypeLabel(info.row.original)}
          workspaceId={workspaceId}
          personSingular={personSingular}
        />
      ),
    }),
    helper.display({
      id: 'name',
      header: 'Name',
      cell: (info) => <NameCell row={info.row.original} workspaceId={workspaceId} />,
    }),
    helper.accessor((row) => row.log.timestamp, {
      id: 'timestamp',
      header: 'Timestamp',
      cell: (info) => <TimestampCell value={info.getValue()} />,
    }),
  ];
}
